/**
 * file cntains helper functions for schema-form service
 */

import {
  get,
  set,
  has,
  startCase,
  merge,
  kebabCase,
  isObject,
  isArray,
  isBoolean,
  isFunction,
  isEmpty,
  isString,
  negate,
  toLower,
  omit,
  map,
} from 'lodash';
import { FormGroup, AbstractControl } from '@angular/forms';
import { rx, Observable, observableOf, observableDefer } from '@shared/rx';
import {
  PredicateFn,
  Primitive,
  Condition,
  IFieldPreferences,
  IFormGroupPreferences,
  OptionTextProperty,
  SchemaNode,
  FieldList,
  IFormRules,
  ToolbarButtonDefinition,
  IFormAction,
  WIDGET_COMPONENT,
} from '@shared/types';
import { isObservable } from '@shared/utils';

/**
 * Returns default value for widget based on schema value type
 *
 * @param field - schema field
 * @returns default value for widget
 *
 */
export const getDefaultValueForField: Function = (field: any): any => {
  if (field.oneOf || field.enum || isReference(field)) {
    return null;
  }
  switch (field.type) {
    case 'array':
      return [];
    case 'number':
    case 'integer':
      return null;
    case 'boolean':
      return false;
    default:
      return '';
  }
};

/**
 * extracting paths for every node in schema using flat recursion
 *
 * @param schema - JSON schema
 * @returns
 */
export const extractSchemaFields: Function = (
  schema: SchemaNode,
): FieldList => {
  const fieldList: FieldList = {};
  if (!!get(schema, 'properties')) {
    Object.keys(schema.properties).forEach((key) => {
      fieldList[key] = schema.properties[key];
      const childList: any = {};

      if (schema.properties[key].type === 'object') {
        Object.entries(extractSchemaFields(schema.properties[key])).forEach(
          ([prop, def]) => (childList[`${key}.${prop}`] = def),
        );
      }
      if (
        schema.properties[key].type === 'array' &&
        get(schema, ['properties', key, 'items', 'properties'])
      ) {
        Object.entries(
          extractSchemaFields(schema.properties[key].items),
        ).forEach(([prop, def]) => (childList[`${key}.0.${prop}`] = def));
      }

      Object.assign(fieldList, childList);
    });
  }

  return fieldList;
};

/**
 * return widget type based on field object
 */
export const preferredFieldWidgetType: Function = (
  field: IFieldPreferences,
): WIDGET_COMPONENT => {
  if (field.type === 'object') return null;
  if (
    field.enum ||
    field.selectOptions ||
    (field.oneOf && Array.isArray(field.oneOf))
  ) {
    return WIDGET_COMPONENT.select;
  }
  if (field.type === 'boolean') return WIDGET_COMPONENT.checkbox;
  if (field.type === 'number' || field.type === 'integer') {
    return WIDGET_COMPONENT.input_number;
  }
  if (isReference(field)) return WIDGET_COMPONENT.entity_select;

  // TODO: Turn it on once we have entity list fetched from API
  // if (name.endsWith('_entity_id') ||
  //   name.endsWith('_entity_ids')) return 'entity-select';
  return WIDGET_COMPONENT.input;
};

/**
 * updates field preference with widget specific data,
 *
 * @param field
 * @returns
 */
export const addWidgetSpecificData: any = (
  field: IFieldPreferences,
): IFieldPreferences => {
  let defaultValues: object = {};
  const overrides: object = {};
  switch (field.widget) {
    case WIDGET_COMPONENT.panel_group:
      defaultValues = {
        show: true,
        widgetData: { groupId: field.name },
      };
      break;
    case WIDGET_COMPONENT.toolbar:
    case WIDGET_COMPONENT.panel:
      defaultValues = { show: true };
      break;
    case WIDGET_COMPONENT.entity_select:
      const fieldNameBlocks: any = get(field, 'name', '').split('_entity_id');
      defaultValues = {
        multiselect: fieldNameBlocks.pop() === 's',
        widgetData: {
          entity: kebabCase(fieldNameBlocks.shift()),
        },
      };
      break;
    default:
  }

  return merge(defaultValues, field, overrides);
};

export const getOptionText: Function = (
  optionText: OptionTextProperty,
  value: any,
  defaultValue: any,
): string =>
  isFunction(optionText)
    ? optionText(value)
    : get(optionText, value, defaultValue);

export const composeSelectOptions = (
  field: IFieldPreferences = {},
): IFieldPreferences => {
  // do not change anything in case when field already has selectOptions property
  if (has(field, 'selectOptions')) {
    return field;
  }

  // there are couple of ways to provide the enum options with a schema
  const schemaEnum = get(field, 'enum', get(field, 'items.enum'));
  if (Array.isArray(schemaEnum)) {
    const selectOptions = schemaEnum.map((value) => ({
      value,
      text: getOptionText(field.optionText, value, startCase(value)),
    }));

    return Object.assign({ selectOptions }, field);
  }

  // enum provided as oneOf field with descriptions
  // optionText still can be provided by form rules and
  // will take precedence over server side description.
  // Uses startCased value as the default when neither
  // description nor optionText is available
  if (Array.isArray(field.oneOf)) {
    const selectOptions = field.oneOf.map((item) => {
      const value = get(item, 'enum.0', '');

      return {
        value,
        text: getOptionText(
          field.optionText,
          value,
          get(item, 'description', startCase(value)),
        ),
      };
    });

    return Object.assign({ selectOptions }, field);
  }

  return field;
};

export const resolveGroupLinks = (
  group: IFormGroupPreferences,
  index: number,
  allGroups: IFormGroupPreferences[],
): void => {
  group.fields.forEach((fieldProps) => {
    const groupId = get(fieldProps, 'widgetData.groupId');
    if (groupId) {
      set(
        fieldProps,
        'widgetData.group',
        allGroups.find(({ id }) => id === groupId),
      );
      set(fieldProps, 'widgetData.group.hidden', true);
    }
  });
};

export const getFormFieldValue = (form: FormGroup, fieldPath: string): any => {
  if (!form) {
    throw Error(
      'getFormFieldControl: property "form" does not exist in editor',
    );
  }

  return form?.get(fieldPath)?.value ?? null;
};

export const setFormFieldValue = <T>(
  form: FormGroup | AbstractControl,
  fieldPath: string,
  value: T,
  markField: boolean = true,
): void => {
  const control: AbstractControl = form?.get(fieldPath);
  setFormControlValue(control, value, markField);
};

export const setFormControlValue = <T>(
  control: AbstractControl,
  value: T,
  markField: boolean = true,
): void => {
  control?.setValue(value);

  if (!markField || !control) return;

  control.markAsTouched();
  control.markAsDirty();
};

export const getFormControl = (
  form: FormGroup,
  fieldPath: string,
): null | AbstractControl => {
  if (!form) return null;

  return fieldPath === '' ? form : form.get(fieldPath);
};

/**
 * returns true if field at provided short path is required in JSON scheme
 */
export const isRequired = (schema, fieldPath: string): boolean => {
  const [propertyName, ...parentPathParts] = fieldPath.split('.').reverse();
  const requiredPath = parentPathParts
    .reverse() // eslint-disable-line
    .map((item) => `properties.${item}.`)
    .join('')
    .concat('required');

  return get(schema, requiredPath, []).includes(propertyName);
};

/**
 * Creates defered observable starting with the form.value on the moment of subscription
 */
export const createDeferedValueObservable = (
  form: FormGroup,
): Observable<any> =>
  observableDefer(() =>
    form.valueChanges.pipe(rx.startWith(form.value), rx.debounceTime(0)),
  );

/** returns an observable for dynamic value based on provided parameter
 * When observable is passed - returns it as is
 * Returns mapped formValue observable when passed argument is a function.
 * Returns; an observableOf provided value in any other case.
 */
export const dynamicPropertyToObservable = (
  formValueObs: Observable<any>,
  dynamicProperty: Observable<any> | Function | any,
): Observable<any> => {
  if (isObservable(dynamicProperty)) {
    return dynamicProperty;
  }
  const normalized = normalizeDynamicProperty(dynamicProperty);

  return isFunction(normalized)
    ? formValueObs.pipe(rx.map(normalized), rx.share())
    : observableOf(Boolean(normalized));
};

const valueMatches = (
  expectation: Primitive | Primitive[],
  actualValue: any,
): boolean => {
  // return true if one of the arrays is a subset of the second
  if (isArray(actualValue) && isArray(expectation)) {
    return (
      [].concat(expectation).every((value) => actualValue.includes(value)) ||
      [].concat(actualValue).every((value) => expectation.includes(value))
    );
  }

  // return true if some of the array value does not equal to expected
  if (isArray(actualValue) && isBoolean(expectation)) {
    return []
      .concat(actualValue)
      .some((value) => expectation === Boolean(value));
  }

  // When expected value is a Boolean, then cast any value to Boolean type
  // For a strict boolean check the array notation can be used e.g. ([true])
  if (isBoolean(expectation)) {
    return expectation === Boolean(actualValue);
  }

  // when field value is an array - return true if every provided values are included.
  if (isArray(actualValue)) {
    return [].concat(expectation).every((value) => actualValue.includes(value));
  }

  // Treat array as allowed values for a non-array form fields
  if (isArray(expectation)) {
    return expectation.includes(actualValue);
  }

  // default value matcher
  return expectation === actualValue;
};

/**
 * Converts object notation to a function if needed.
 * Converts to a boolean any input value which is neither an object nor a function
 */
export const normalizeDynamicProperty = (
  input: PredicateFn | Condition | Condition[] | Primitive | null | undefined, // eslint-disable-line
): PredicateFn | boolean => {
  if (isFunction(input)) return input;
  if (isObject(input)) return objectPredicateToFunction(input);

  return Boolean(input);
};

export const applyDynamicButtonProperties = (
  originalButtons: ToolbarButtonDefinition[],
  formValue: any,
  formAction?: IFormAction,
): ToolbarButtonDefinition[] => {
  const buttons = map(originalButtons, (button) => {
    const staticButton = {
      ...button,
      // adds support for object notation for disabled and hidden
      disabled: normalizeDynamicProperty(button.disabled),
      hidden: normalizeDynamicProperty(button.hidden),
    };
    // call functions for dynamic properties
    ['caption', 'disabled', 'hidden'].forEach((propertyName) => {
      if (isFunction(staticButton[propertyName])) {
        set(
          staticButton,
          propertyName,
          staticButton[propertyName].call(null, formValue, formAction),
        );
      }
    });

    return staticButton;
  });

  return buttons;
};

export const filterConditions = (conditions: Condition[]): Condition[] =>
  conditions.filter(isObject).filter(negate(isEmpty));

export const deepConditionCheck = (
  key: string,
  expectation,
  formValue: Object,
): boolean => {
  const check = (condition): boolean => checkCondition(condition, formValue);

  switch (key) {
    case 'not':
      return !check(expectation);
    case 'oneOf':
      return (
        1 ===
        filterConditions(expectation).reduce(
          (count, condition) => count + +check(condition),
          0,
        )
      );
    case 'allOf':
      return filterConditions(expectation).every(check);
    case 'anyOf':
      return filterConditions(expectation).some(check);
    default:
      const valueFromForm = get(formValue, key);

      return valueMatches(expectation, valueFromForm);
  }
};

export const checkCondition = (condition: Condition, formValue): boolean => {
  return Object.entries(condition).every(([key, expectation]) =>
    deepConditionCheck(key, expectation, formValue),
  );
};

export const objectPredicateToFunction = (
  config: Condition | Condition[],
): PredicateFn => {
  // multiple condition sets may be provided as well as a single one
  // .every() returns true when used on an empty array so keep only non-empty objects
  const conditions = filterConditions([].concat(config));

  return (formValue: Object): boolean => {
    return conditions.some((condition) => checkCondition(condition, formValue));
  };
};

/**
 * Return true if field is a reference to another entity
 *
 * @param field
 */
export const isReference = (field: IFieldPreferences): boolean => {
  return get(field, 'objectid_convert', false);
};

/**
 * Return field without description field if it is equal to title field or title contains the whole description
 */
export const removeRedundantDescriptions = (
  field: IFieldPreferences,
): IFieldPreferences => {
  if (!isString(field?.description)) return field;

  const description = toLower(field?.description);
  const title = toLower(field?.title);

  return title.includes(description) ? omit(field, 'description') : field;
};

/**
 * Composes and returns a partial schema build from any UI custom fields, that have schema node.
 * These fields are not included in the original schema, however,
 * these properties do exist in the populated documents.
 * To attach a widget to this field the schema should have such property,
 */
export const getLocalSchemaProperties = (rules: IFormRules): SchemaNode => {
  const schemaProperties = {};

  Object.entries(get(rules, 'fields', {})).forEach(([fieldPath, node]) => {
    const schemaNode = get(node, 'schema');
    if (schemaNode) {
      const schemaPath = fieldPath.split('.').join('.properties.');
      set(schemaProperties, schemaPath, schemaNode);
    }
  });

  return schemaProperties;
};

export const getPropertyFromSchemaPath = (
  schemaNode: SchemaNode,
  pathFromUI: string,
  resultedPath: string[] = [],
): string[] => {
  if (isEmpty(pathFromUI)) return resultedPath;
  const [currentNode, ...parsedPath] = pathFromUI.split('.');
  const currentNodeInItems = ['items', currentNode];
  const currentNodeInProperties = ['properties', currentNode];

  // check if current property contains in current schema node
  // AND it's deepest item in path
  // AND item does not contain in `properties` and `items`
  // (e.g. calc.type, should not pick properties.calc.type, should pick properties.calc.properties.type)
  if (
    has(schemaNode, currentNode) &&
    isEmpty(parsedPath) &&
    !(
      has(schemaNode, currentNodeInProperties) ||
      has(schemaNode, currentNodeInItems)
    )
  ) {
    return [...resultedPath, currentNode];
  }

  if (has(schemaNode, currentNodeInProperties)) {
    return getPropertyFromSchemaPath(
      get(schemaNode, currentNodeInProperties),
      parsedPath.join('.'),
      [...resultedPath, ...currentNodeInProperties],
    );
  }

  if (has(schemaNode, currentNodeInItems)) {
    return getPropertyFromSchemaPath(
      get(schemaNode, currentNodeInItems),
      parsedPath.join('.'),
      [...resultedPath, ...currentNodeInItems],
    );
  }

  return resultedPath;
};
